import React, { useState, useEffect, useCallback } from 'react';

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
}

interface CherryBlossomParticlesProps {
  onParticleClick: (points: number, x: number, y: number) => void;
}

const CherryBlossomParticles: React.FC<CherryBlossomParticlesProps> = ({ onParticleClick }) => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [nextId, setNextId] = useState(0);

  // Create a new particle
  const createParticle = useCallback(() => {
    const viewportWidth = Math.min(window.innerWidth, document.documentElement.clientWidth);
    const documentHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
    
    const particle: Particle = {
      id: nextId,
      x: Math.random() * viewportWidth,
      y: Math.random() * documentHeight,
      size: Math.random() * 12 + 8, // 8-20px (increased minimum from 4 to 8)
      speedX: (Math.random() - 0.5) * 1.2, // -0.6 to 0.6
      speedY: (Math.random() - 0.5) * 1.2,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 2,
      opacity: Math.random() * 0.5 + 0.5 // 0.5 to 1
    };
    
    setParticles(prev => [...prev, particle]);
    setNextId(prev => prev + 1);
  }, [nextId]);

  // Spawn particles periodically
  useEffect(() => {
    const spawnInterval = setInterval(() => {
      if (particles.length < 200) { // Increased limit to 200 particles
        createParticle();
      }
    }, 300); // Spawn every 0.3 seconds for more frequent spawning

    return () => clearInterval(spawnInterval);
  }, [particles.length, createParticle]);

  // Animate particles
  useEffect(() => {
    const animateParticles = () => {
      const viewportWidth = Math.min(window.innerWidth, document.documentElement.clientWidth);
      const documentHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      
      setParticles(prev => 
        prev.map(particle => {
          let newX = particle.x + particle.speedX;
          let newY = particle.y + particle.speedY;
          
          // Wrap around document edges (constrained to viewport width, full document height)
          if (newX > viewportWidth + 20) newX = -20;
          if (newX < -20) newX = viewportWidth + 20;
          if (newY > documentHeight + 20) newY = -20;
          if (newY < -20) newY = documentHeight + 20;
          
          return {
            ...particle,
            x: newX,
            y: newY,
            rotation: particle.rotation + particle.rotationSpeed,
          };
        })
      );
    };

    const animationInterval = setInterval(animateParticles, 50); // 20 FPS
    return () => clearInterval(animationInterval);
  }, []);

  // Handle window resize to keep particles in document bounds
  useEffect(() => {
    const handleResize = () => {
      const viewportWidth = Math.min(window.innerWidth, document.documentElement.clientWidth);
      const documentHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      
      setParticles(prev => 
        prev.map(particle => {
          // Keep particles within new document bounds
          const newX = Math.min(Math.max(particle.x, 0), viewportWidth);
          const newY = Math.min(Math.max(particle.y, 0), documentHeight);
          
          return {
            ...particle,
            x: newX,
            y: newY,
          };
        })
      );
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle particle click
  const handleParticleClick = (particleId: number, x: number, y: number) => {
    setParticles(prev => prev.filter(p => p.id !== particleId));
    onParticleClick(15, x, y);
  };

  return (
    <div className="absolute inset-0 pointer-events-none z-40 overflow-hidden">
      {particles.map(particle => (
        <div
          key={particle.id}
          className="absolute cursor-pointer pointer-events-auto transition-opacity duration-300 hover:opacity-80"
          style={{
            left: `${particle.x}px`,
            top: `${particle.y}px`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            transform: `rotate(${particle.rotation}deg)`,
            opacity: particle.opacity,
            maxWidth: '20px',
            maxHeight: '20px',
            zIndex: 40,
          }}
          onClick={() => handleParticleClick(particle.id, particle.x, particle.y)}
        >
          <div 
            className="w-full h-full bg-pink-300 rounded-sm shadow-sm"
            style={{
              background: 'linear-gradient(45deg, #f9a8d4, #ec4899)',
              boxShadow: '0 2px 4px rgba(236, 72, 153, 0.3)'
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default CherryBlossomParticles; 